import styled from 'styled-components';
import { PaymentMethod, PaymentPlanDetailResult } from '@mews-commander/data-access';
import { Card, getToken, Grid, GridItem, Stack, Typography } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedEnum, lt } from '@mews/framework';
import { Dash, LocalizedDateFromUtc, SpacedDash } from '@mews-commander/ui';
import { WithAccountId } from '@mews-commander/types';
import { PaymentPlanStatusTag } from '../../tables/PaymentPlans/PaymentPlanStatusTag';
import { useGetUpcomingPaymentText } from '../../tables/PaymentPlans/hooks';
import { PaymentMethodType } from '../../tables/PaymentPlans/PaymentMethodType';
import { PaymentMethodStateTag } from '../../common/PaymentMethodStateTag';
import { showNextScheduledPayment } from '../../tables/PaymentPlans/utils';
import { EmptyPaymentMethodStatusTag } from '../../tables/PaymentMethods/EmptyPaymentMethodStatusTag';
import { PaymentRequestStateTag } from '../../tables/PaymentRequest/PaymentRequestStateTag';
import { PaymentPlanReservationLink } from './PaymentPlanReservationLink';
import { PaymentPlanDetailItem } from './PaymentPlanDetailItem';

export const PaymentPlanDetailsViewDetailSection = ({
    detail,
    accountId,
}: { detail: PaymentPlanDetailResult } & WithAccountId) => {
    const {
        status,
        nextPaymentAmount,
        nextPaymentDateUtc,
        assignedSpace,
        service,
        billingMode,
        rate,
        paymentMethodData,
        frequency,
        createdDateUtc,
        startDateUtc,
        endDateUtc,
        reservationStartDateUtc,
        reservationEndDateUtc,
        initialPaymentRequestState,
    } = detail;

    const getUpcomingPaymentText = useGetUpcomingPaymentText();

    return (
        <Card variant="subtle" padding="200" outline={false}>
            <Grid templateColumns="auto 1fr" columnGap="500" rowGap="200">
                <GridItem column="span 2">
                    <Typography textStyle="bodyMediumStrong" variant="primary" as="div">
                        {lt(TextKey.DetailPlural)}
                    </Typography>
                </GridItem>

                <GridItem column="span 2">
                    <Stack vertical spacing="100">
                        <Typography textStyle="bodyLarge" variant="tertiary" as="div">
                            {lt(TextKey.PaymentPlanDetailsCardSectionNextPayment)}
                        </Typography>
                        <Typography textStyle="titleSmallStrong" as="div">
                            {showNextScheduledPayment(detail) && nextPaymentAmount && nextPaymentDateUtc ? (
                                getUpcomingPaymentText(nextPaymentAmount, nextPaymentDateUtc)
                            ) : (
                                <Dash />
                            )}
                        </Typography>
                    </Stack>
                </GridItem>

                <PaymentPlanDetailsGridDivider />

                <PaymentPlanDetailItem
                    description={TextKey.Reservation}
                    value={<PaymentPlanReservationLink detail={detail} />}
                />
                <PaymentPlanDetailItem
                    description={TextKey.Status}
                    value={<PaymentPlanStatusTag status={status} outline={false} />}
                />
                <PaymentPlanDetailItem
                    description={TextKey.PaymentMethod}
                    value={
                        paymentMethodData ? (
                            <Stack spacing="100" itemAlignment="center">
                                <PaymentMethodType paymentMethodData={paymentMethodData} accountId={accountId} />
                                <PaymentMethodStateTag
                                    paymentMethod={paymentMethodData.discriminator}
                                    state={paymentMethodData.value.state}
                                    outline={false}
                                />
                            </Stack>
                        ) : (
                            <EmptyPaymentMethodStatusTag
                                paymentRequestState={initialPaymentRequestState}
                                outline={false}
                            />
                        )
                    }
                />
                <PaymentPlanDetailItem
                    description={TextKey.PaymentRequest}
                    value={
                        initialPaymentRequestState ? (
                            <PaymentRequestStateTag state={initialPaymentRequestState} outline={false} />
                        ) : (
                            <Dash />
                        )
                    }
                />
                {paymentMethodData?.discriminator === PaymentMethod.SepaDirectDebit && (
                    <PaymentPlanDetailItem
                        description={TextKey.PaymentPlanDetailMandateReference}
                        value={paymentMethodData.value.mandateReference}
                    />
                )}

                <PaymentPlanDetailsGridDivider />

                <PaymentPlanDetailItem description={TextKey.Space} value={assignedSpace} />
                <PaymentPlanDetailItem description={TextKey.Service} value={service} />
                <PaymentPlanDetailItem
                    description={TextKey.BillingMode}
                    value={<LocalizedEnum prefix="PaymentPlanBillingMode" value={billingMode} />}
                />
                <PaymentPlanDetailItem description={TextKey.Rate} value={rate} />

                <PaymentPlanDetailsGridDivider />

                <PaymentPlanDetailItem description={TextKey.Frequency} value={frequency} />
                <PaymentPlanDetailItem
                    description={TextKey.CreatedDate}
                    value={<LocalizedDateFromUtc utcString={createdDateUtc} />}
                />
                <PaymentPlanDetailItem
                    description={TextKey.PaymentPlanDetailsLabelPlanDates}
                    value={
                        <>
                            <LocalizedDateFromUtc utcString={startDateUtc} />
                            {SpacedDash}
                            <LocalizedDateFromUtc utcString={endDateUtc} />
                        </>
                    }
                />
                <PaymentPlanDetailItem
                    description={TextKey.PaymentPlanDetailsLabelReservationDates}
                    value={
                        <>
                            <LocalizedDateFromUtc utcString={reservationStartDateUtc} />
                            {SpacedDash}
                            <LocalizedDateFromUtc utcString={reservationEndDateUtc} />
                        </>
                    }
                />
            </Grid>
        </Card>
    );
};

const PaymentPlanDetailsGridDivider = () => (
    <GridItem column="span 2">
        <PaymentPlanDetailsDivider />
    </GridItem>
);

// TODO: use `Divider` when it supports more colors, added with DS blessing
const PaymentPlanDetailsDivider = styled.div`
    border: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
`;
